import { useField } from 'formik';
import { isString } from 'lodash-es';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDebounce } from 'react-use';

import * as constants from 'utils/constants';

import { ComponentProps, TextAreaComponentProps } from './interfaces';
import * as Styled from './styled';

export const TextArea = React.forwardRef<HTMLFormElement, TextAreaComponentProps>(
  function TextAreaBase(
    {
      ariaLabel = '',
      hint,
      label,
      mode = 'Normal',
      maxLength = 100,
      maxRows = 3,
      name,
      error,
      errorLabelProps,
      ...props
    },
    ref
  ) {
    return (
      <Styled.Wrapper>
        <Styled.Label htmlFor={name} id={`${name}.label`}>
          {label}
        </Styled.Label>
        <Styled.TextArea
          ref={ref}
          name={name}
          mode={mode}
          minRows={mode === 'Input' ? 1 : 6}
          maxRows={mode === 'Input' ? maxRows : Number.POSITIVE_INFINITY}
          aria-label={ariaLabel || label}
          aria-invalid={!!error}
          aria-labelledby={label ? `${name}.label` : props['aria-labelledby']}
          maxLength={maxLength}
          error={error ? 'true' : 'false'}
          {...props}
        />
        {error && <Styled.ErrorLabel {...errorLabelProps}>{error}</Styled.ErrorLabel>}
        {hint && <Styled.HintLabel>{hint}</Styled.HintLabel>}
      </Styled.Wrapper>
    );
  }
);

const TextAreaWithFormik: React.FC<ComponentProps> = ({
  delay = constants.INPUT_DELAY,
  name,
  ...props
}) => {
  const [field, meta, fieldHelpers] = useField(name);
  const [innerValue, setInnerValue] = useState('');
  const changeEventRef = useRef<React.ChangeEvent<HTMLTextAreaElement>>();

  /**
   * Displaying a user error message
   */
  const showError = meta.touched && !!meta.error;

  const { value, onBlur } = field;

  // Add a "blur" event that will automatically trim all whitespaces from left & right sides of the string followed by triggering the original "field" onBlur
  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      e.preventDefault();
      if (isString(value)) {
        fieldHelpers.setValue(value.trim());
      }
      onBlur(e);
    },
    [value, onBlur, fieldHelpers]
  );

  /**
   * Using a debounce efect for the input to avoid having the field calling handleChange on every key stroke.
   * ref:
   * https://medium.com/@vlad_19457/reducing-the-number-of-renders-when-using-formik-9790bf111ab9)
   * https://github.com/streamich/react-use/blob/master/docs/useDebounce.md
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isReady, cancel] = useDebounce(
    () => {
      if (changeEventRef.current) {
        field.onChange(changeEventRef.current);
      }
    },
    delay,
    [changeEventRef.current]
  );

  // Effect to update the input field internal value
  useEffect(() => {
    if (value) {
      setInnerValue(value as string);
    } else {
      setInnerValue('');
    }
  }, [value]);

  /**
   * Event handler when input value gets changed
   */
  const handleOnChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.persist();

    const newValue = event.currentTarget.value;
    setInnerValue(newValue);
    changeEventRef.current = event;
  }, []);

  return (
    <TextArea
      {...props}
      name={field.name}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      error={showError ? meta.error : undefined}
      value={innerValue}
    />
  );
};

export default TextAreaWithFormik;
